/**
 * YouTube Downloader Service
 * 
 * This service provides functions to interact with the YouTube downloader API.
 */

// API Base URL - should be configured based on environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// API Endpoints
const ENDPOINTS = {
  INFO: `${API_BASE_URL}/youtube/info`,
  DOWNLOAD: `${API_BASE_URL}/youtube/download`,
  STATUS: `${API_BASE_URL}/youtube/status`,
};

/**
 * @typedef {Object} VideoFormat
 * @property {string} format_id - Format identifier
 * @property {string} extension - File extension (e.g., mp4, webm)
 * @property {string} resolution - Video resolution (e.g., 720p, 1080p)
 * @property {number} filesize - File size in bytes
 * @property {string} note - Additional format information
 */

/**
 * @typedef {Object} VideoInfo
 * @property {string} video_id - YouTube video ID
 * @property {string} title - Video title
 * @property {string} description - Video description
 * @property {number} duration - Video duration in seconds
 * @property {string} thumbnail - URL to video thumbnail
 * @property {VideoFormat[]} formats - Available download formats
 */

/**
 * @typedef {Object} DownloadTask
 * @property {string} task_id - Unique identifier for the download task
 * @property {string} message - Status message
 */

/**
 * @typedef {Object} TaskStatus
 * @property {string} task_id - Unique identifier for the download task
 * @property {string} status - Current status (queued, processing, completed, failed)
 * @property {number} progress - Download progress percentage (0-100)
 * @property {Object} result - Download result data (available when completed)
 * @property {string} error - Error message (available when failed)
 */

/**
 * @typedef {Object} DownloadOptions
 * @property {string} format_id - Optional format ID to download
 * @property {string} quality - Quality level (best, high, medium, low, worst)
 * @property {boolean} audio_only - Whether to download audio only
 */

/**
 * Generic error handler for API requests
 * 
 * @param {Response} response - Fetch API Response object
 * @returns {Promise<any>} - Parsed JSON response or throws an error
 */
async function handleResponse(response) {
  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.detail || 'An error occurred');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

/**
 * Get information about a YouTube video
 * 
 * @param {string} url - YouTube video URL
 * @returns {Promise<VideoInfo>} - Video information
 */
export async function getVideoInfo(url) {
  try {
    const response = await fetch(ENDPOINTS.INFO, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching video info:', error);
    throw error;
  }
}

/**
 * Start downloading a YouTube video
 * 
 * @param {string} url - YouTube video URL
 * @param {DownloadOptions} options - Download options
 * @returns {Promise<DownloadTask>} - Download task information
 */
export async function downloadVideo(url, options = {}) {
  try {
    const { format_id, quality = 'best', audio_only = false } = options;
    
    const response = await fetch(ENDPOINTS.DOWNLOAD, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url,
        format_id,
        quality,
        audio_only,
      }),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error starting download:', error);
    throw error;
  }
}

/**
 * Check the status of a download task
 * 
 * @param {string} taskId - Task ID to check
 * @returns {Promise<TaskStatus>} - Task status information
 */
export async function getTaskStatus(taskId) {
  try {
    const response = await fetch(`${ENDPOINTS.STATUS}/${taskId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error checking task status:', error);
    throw error;
  }
}

/**
 * Poll the status of a download task at regular intervals
 * 
 * @param {string} taskId - Task ID to check
 * @param {function} onStatusUpdate - Callback to handle status updates
 * @param {number} interval - Polling interval in milliseconds
 * @returns {Object} - Controller with stop method to cancel polling
 */
export function pollTaskStatus(taskId, onStatusUpdate, interval = 1000) {
  let timer = null;
  let stopped = false;
  
  const checkStatus = async () => {
    if (stopped) return;
    
    try {
      const status = await getTaskStatus(taskId);
      onStatusUpdate(status);
      
      // If download is complete or failed, stop polling
      if (['completed', 'failed'].includes(status.status)) {
        stop();
        return;
      }
      
      // Continue polling
      timer = setTimeout(checkStatus, interval);
    } catch (error) {
      console.error('Error polling task status:', error);
      onStatusUpdate({ status: 'failed', error: error.message });
      stop();
    }
  };
  
  const stop = () => {
    stopped = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  
  // Start polling
  checkStatus();
  
  // Return controller
  return { stop };
}

/**
 * Format file size in human-readable format
 * 
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string} - Formatted file size
 */
export function formatFileSize(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Format duration in human-readable format
 * 
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration (MM:SS or HH:MM:SS)
 */
export function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts = [
    minutes.toString().padStart(2, '0'),
    secs.toString().padStart(2, '0')
  ];
  
  if (hours > 0) {
    parts.unshift(hours.toString());
  }
  
  return parts.join(':');
}

export default {
  getVideoInfo,
  downloadVideo,
  getTaskStatus,
  pollTaskStatus,
  formatFileSize,
  formatDuration,
};

